import {
  LayoutGrid,
  File,
  Palette,
  LayoutPanelLeft,
  ExternalLink,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";

type MentionBadgeType = "project" | "file" | "theme" | "skill";

interface MentionBadgeProps {
  type: MentionBadgeType;
  name: string;
  id?: string;
  onRemove?: () => void;
  /** Use "onPrimary" when the badge is rendered on a primary-colored background (user chat bubble) */
  variant?: "default" | "onPrimary";
}

const STYLES: Record<MentionBadgeType, string> = {
  file: "bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:bg-blue-400/10 dark:border-blue-400/25 dark:text-blue-300",
  project:
    "bg-violet-500/10 border border-violet-500/30 text-violet-600 dark:bg-violet-400/10 dark:border-violet-400/25 dark:text-violet-300",
  theme:
    "bg-pink-500/10 border border-pink-500/30 text-pink-600 dark:bg-pink-400/10 dark:border-pink-400/25 dark:text-pink-300",
  skill:
    "bg-purple-500/10 border border-purple-500/30 text-purple-600 dark:bg-purple-400/10 dark:border-purple-400/25 dark:text-purple-300",
};

// When rendered on a primary-colored background (user chat bubble), use white/translucent styles
const ON_PRIMARY_STYLE = "bg-white/15 border border-white/30 text-white";

const ICONS: Record<MentionBadgeType, typeof LayoutGrid> = {
  project: LayoutGrid,
  file: File,
  theme: Palette,
  skill: LayoutPanelLeft,
};

export function MentionBadge({
  type,
  name,
  id,
  onRemove,
  variant = "default",
}: MentionBadgeProps) {
  const Icon = ICONS[type];

  const handleOpen = () => {
    if (type === "project" && id) {
      window.open(`/dashboard/editor/${id}`, "_blank");
    } else if (type === "skill") {
      // id is the routing slug; custom- prefix means it's a custom skill
      window.open(`/dashboard/skills`, "_blank");
    }
  };

  const isClickable = (type === "project" && !!id) || type === "skill";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium shadow-sm",
        variant === "onPrimary" ? ON_PRIMARY_STYLE : STYLES[type],
      )}
    >
      <Icon className="w-3.5 h-3.5 opacity-80 flex-shrink-0" />
      {isClickable ? (
        <button
          type="button"
          onClick={handleOpen}
          className="hover:underline"
          title={type === "skill" ? `Skill: ${name}` : `Project: ${name}`}
        >
          {name}
        </button>
      ) : (
        <span>{name}</span>
      )}
      {isClickable && !onRemove && (
        <ExternalLink className="w-2.5 h-2.5 opacity-60" />
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 opacity-70 hover:opacity-100"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}
